import type { CancelTokenSource } from 'axios';
import { create } from 'zustand';

import * as metrics from 'lib/metrics';
import type { ServerError } from 'models';
import type { CryptoOperation, CryptoProvider } from 'models/crypto';
import type {
    DepositTrackingRequestProps,
    MeshIntegrationToken,
    MeshWithdrawalAddress,
    WithdrawalSource,
} from 'models/reqres';
import { useCryptoCitizenshipDialogStore } from 'stores/zustand/cryptoCitizenshipDialogStore';
import { useScrollLockStore } from 'stores/zustand/scrollLockStore';
import { handleSnackbarError } from 'stores/zustand/snackbarStore';
import config from 'utils/config';
import type { MeshLinkHandle } from 'utils/crypto';
import { getDepositRequestTrackingProps } from 'utils/depositTracking';
import { fetchSardineSessionKey } from 'utils/sardine';

import {
    createCryptoAccount as createCryptoAccountRequest,
    createCryptoWithdrawal,
    getCryptoAccessToken,
    getCryptoAccountDetails as getCryptoAccountDetailsRequest,
    getMeshLinkToken,
    getMeshWithdrawalAddresses,
    initiateMeshWithdrawal as initiateMeshWithdrawalRequest,
} from '../../transport/domains/crypto';

// The rail vendor for crypto, resolved by callers via
// payments.getEnabledVendorForRail('crypto', ...) and passed into
// createTransfer. Kept out of this store so it stays decoupled from the
// payments store.
type CryptoRailVendor = 'stripe' | 'zerohash' | 'coinbase' | null;

// Non-reactive imperative handles — no component renders off these, so they
// live at module scope rather than in the reactive store state.
let tokenTimer: NodeJS.Timeout | null = null;
let tokenFetch: CancelTokenSource | null = null;
let accessTokenFetch: CancelTokenSource | null = null;
// Invalidated on session teardown so late callbacks cannot mutate the next user's flow.
let flowGeneration = 0;

/**
 * Whether *this* flow currently holds the global scroll lock.
 *
 * `scrollLockStore` is a single boolean shared by every overlay and is not ref-counted, so the
 * Mesh teardown must release only a lock it actually took. Unlocking unconditionally would free
 * the page behind an unrelated overlay that is still mounted (a search modal, say) whose
 * mount-only locking effect will not re-run.
 */
let meshHoldsScrollLock = false;
/**
 * The live Mesh Link instance, if one is open.
 *
 * Link is an external SDK widget: bumping `flowGeneration` cannot stop it, and its callbacks in
 * `MeshModals` outlive a logout — `onExit` could reopen a dialog and `onIntegrationConnected`
 * samples the generation at call time, so it would pass the guard with the *new* session's value
 * and repopulate address state. Hold the handle so a session end can close it.
 */
let activeMeshLink: MeshLinkHandle | null = null;

function depositTrackingPropsMatch(
    left: DepositTrackingRequestProps | undefined,
    right: DepositTrackingRequestProps | undefined,
): boolean {
    return left?.deposit_flow_id === right?.deposit_flow_id && left?.frontend_session_id === right?.frontend_session_id;
}

interface CryptoFlowState {
    loading: boolean;
    linkToken: string;
    linkTokenUserId: string | undefined;
    linkTokenTrackingProps: DepositTrackingRequestProps | undefined;
    meshLinkToken: string;
    meshWalletAddresses: MeshWithdrawalAddress[];
    selectedMeshAddress: MeshWithdrawalAddress | null;
    actions: {
        getCryptoAccountDetails: (
            userId: string,
            onSuc: (token: string) => void,
            onErr: (err: ServerError) => void,
            trackingProps?: DepositTrackingRequestProps,
        ) => void;
        createCryptoAccount: (
            userId: string,
            citizenship: string,
            onSuc: (token: string) => void,
            onErr: (error: ServerError) => void,
        ) => Promise<void>;
        createTransfer: (
            userId: string,
            vendor: CryptoRailVendor,
            onLaunchCrypto: (
                token: string,
                provider: CryptoProvider,
                meshIntegrationTokens?: MeshIntegrationToken[],
            ) => void,
            onSuc?: () => void,
            onErr?: (err: ServerError) => void,
            operation?: CryptoOperation,
        ) => void;
        createZeroHashTransfer: (
            userId: string,
            onLaunchZeroHash: (token: string, provider: CryptoProvider) => void,
            onSuc?: () => void,
            onErr?: (err: ServerError) => void,
            trackingProps?: DepositTrackingRequestProps,
        ) => void;
        createMeshTransfer: (
            userId: string,
            operation: CryptoOperation,
            onLaunchMesh: (
                token: string,
                provider: CryptoProvider,
                meshIntegrationTokens?: MeshIntegrationToken[],
            ) => void,
            onSuc?: () => void,
            onErr?: (err: ServerError) => void,
            trackingProps?: DepositTrackingRequestProps,
        ) => void;
        getMeshWalletAddresses: (
            userId: string,
            authToken: string,
            type: string,
            symbol: string,
            onSuc: (addresses: MeshWithdrawalAddress[]) => void,
            onErr: (err: ServerError) => void,
        ) => void;
        initiateMeshWithdrawal: (
            userId: string,
            walletAddress: string,
            amountCents: number,
            network: string,
            mfaCode: string,
            onSuc: () => void,
            onErr: (err: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;
        cryptoWithdraw: (
            userId: string,
            walletId: string,
            amountCents: number,
            mfaCode: string,
            onSuc: () => void,
            onErr: (err: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;
        setSelectedMeshAddress: (address: MeshWithdrawalAddress | null) => void;
        setLoading: (loading: boolean) => void;
        disposeLinkToken: () => void;
        disposeMeshLinkToken: () => void;
        dispose: () => void;
        /** Token identifying the current flow generation; callbacks compare against it. */
        currentFlowToken: () => number;
        /** False once a logout (or another flow start) has superseded `token`. */
        isFlowCurrent: (token: number) => boolean;
        /** Registers the live Mesh Link instance so session end can close it. */
        registerMeshLink: (handle: MeshLinkHandle | null) => void;
        /** Takes the global scroll lock for this Mesh flow and records that it owns it. */
        acquireMeshScrollLock: () => void;
        /**
         * Releases the global scroll lock, but only if this Mesh flow took it.
         *
         * `scrollLockStore` is one shared boolean rather than a ref count, so an unconditional
         * release would unlock the page behind an unrelated overlay that is still mounted.
         */
        releaseMeshScrollLock: () => void;
        /**
         * Forgets `handle`, but only while it is still the registered one.
         *
         * `closeLink()` delivers `onExit` asynchronously, so a Link closed by one session can exit
         * after the next user has already opened (and registered) their own. An unconditional
         * clear there would drop the newer handle, and that user's logout would then find nothing
         * to close — leaving their Mesh widget on screen into the logged-out session.
         */
        releaseMeshLink: (handle: MeshLinkHandle | null) => void;
        /**
         * Session-end teardown. Separate from `dispose()`, which also runs when the effects
         * bootstrap unmounts with the user still signed in: clearing the selected Mesh
         * destination there would strand an open amount dialog (its open flag and entered amount
         * live in `withdrawalDialogStore` and survive that teardown), and submitting would then
         * silently close with no address. On a real logout `withdrawalDialogStore.endSession()`
         * clears those flags in the same pass, so the two stay consistent.
         */
        endSession: () => void;
    };
}

export const useCryptoFlowStore = create<CryptoFlowState>()((set, get) => ({
    loading: false,
    linkToken: '',
    linkTokenUserId: undefined,
    linkTokenTrackingProps: undefined,
    meshLinkToken: '',
    meshWalletAddresses: [],
    selectedMeshAddress: null,
    actions: {
        getCryptoAccountDetails: (userId, onSuc, onErr, trackingProps) => {
            if (tokenFetch !== null) return;
            const generation = flowGeneration;
            set({ loading: true });
            tokenFetch = getCryptoAccountDetailsRequest(
                userId,
                (status, code) => {
                    if (generation !== flowGeneration) return;
                    if (code !== '' && status != '') {
                        fetchSardineSessionKey('deposit', userId).then((sardine) => {
                            // `!sardine.ok` means the session that captured `userId` ended
                            // while this was pending; drop the token fetch with it.
                            if (!sardine.ok || generation !== flowGeneration) return;
                            accessTokenFetch = getCryptoAccessToken(
                                userId,
                                (token) => {
                                    if (generation !== flowGeneration) return;
                                    set({
                                        linkToken: token,
                                        linkTokenUserId: userId,
                                        linkTokenTrackingProps: trackingProps,
                                    });
                                    tokenTimer = setInterval(() => {
                                        get().actions.disposeLinkToken();
                                    }, config.tokenExpiration);
                                    onSuc(token);
                                },
                                (error) => {
                                    if (generation === flowGeneration) onErr(error);
                                },
                                () => {
                                    if (generation === flowGeneration) accessTokenFetch = null;
                                },
                                sardine.key || undefined,
                                trackingProps,
                            );
                        });
                    } else {
                        useCryptoCitizenshipDialogStore.getState().actions.setCallbackOperation('link-account');
                        useCryptoCitizenshipDialogStore.getState().actions.open();
                        metrics.recordDepositMetric({
                            step: 'cryptoOpenCitizenShip',
                        });
                    }
                },
                (error) => {
                    if (generation !== flowGeneration) return;
                    onErr(error);
                    handleSnackbarError(error);
                },
                () => {
                    if (generation !== flowGeneration) return;
                    tokenFetch = null;
                    set({ loading: false });
                },
            );
        },

        createCryptoAccount: async (userId, citizenship, onSuc, onErr) => {
            const generation = flowGeneration;
            const sardine = await fetchSardineSessionKey('deposit', userId);
            // Not `ok`: the session that captured `userId` above has ended. Issuing the
            // request now sends the ended user's operation, and its 401 is read against
            // whoever is signed in by then.
            if (!sardine.ok) return;
            const sardineSessionKey = sardine.key;
            if (generation !== flowGeneration) return;
            tokenFetch = createCryptoAccountRequest(
                userId,
                citizenship,
                (status, code) => {
                    if (generation !== flowGeneration) return;
                    if (code !== '' && status != '') {
                        fetchSardineSessionKey('deposit', userId).then((innerSardine) => {
                            if (!innerSardine.ok || generation !== flowGeneration) return;
                            accessTokenFetch = getCryptoAccessToken(
                                userId,
                                (token) => {
                                    if (generation !== flowGeneration) return;
                                    set({
                                        linkToken: token,
                                        linkTokenUserId: userId,
                                        linkTokenTrackingProps: undefined,
                                    });
                                    tokenTimer = setInterval(() => {
                                        get().actions.disposeLinkToken();
                                    }, config.tokenExpiration);
                                    onSuc(token);
                                },
                                undefined,
                                () => {
                                    if (generation === flowGeneration) accessTokenFetch = null;
                                },
                                innerSardine.key || undefined,
                            );
                        });
                    }
                },
                (error) => {
                    if (generation === flowGeneration) onErr(error);
                },
                () => {
                    if (generation !== flowGeneration) return;
                    tokenFetch = null;
                    set({ loading: false });
                },
                sardineSessionKey || undefined,
            );
        },

        // Routes a crypto transfer to its provider-specific flow. Stripe-crypto
        // does NOT belong here — it has its own redirect-based flow; callers
        // must short-circuit on `vendor === 'stripe'` before invoking this.
        createTransfer: (userId, vendor, onLaunchCrypto, onSuc, onErr, operation = 'deposit') => {
            const trackingProps = operation === 'deposit' ? getDepositRequestTrackingProps() : undefined;

            if (vendor === 'stripe') {
                // A stripe-crypto user shouldn't have hit this code path. Bail
                // loudly rather than silently routing to ZeroHash.
                console.warn('crypto.createTransfer called with stripe vendor; caller must handle stripe separately');
                return;
            }

            if (vendor === 'coinbase') {
                get().actions.createMeshTransfer(userId, operation, onLaunchCrypto, onSuc, onErr, trackingProps);
            } else {
                // ZeroHash is the US fallback (and the only remaining vendor here).
                get().actions.createZeroHashTransfer(userId, onLaunchCrypto, onSuc, onErr, trackingProps);
            }
        },

        createZeroHashTransfer: (userId, onLaunchZeroHash, onSuc, onErr, trackingProps) => {
            const { linkToken, linkTokenUserId, linkTokenTrackingProps } = get();
            if (
                linkToken !== '' &&
                linkTokenUserId === userId &&
                depositTrackingPropsMatch(linkTokenTrackingProps, trackingProps)
            ) {
                onLaunchZeroHash(linkToken, 'zerohash');
                if (onSuc) onSuc();
                return;
            }
            if (linkToken !== '') {
                get().actions.disposeLinkToken();
            }
            get().actions.getCryptoAccountDetails(
                userId,
                (token) => {
                    onLaunchZeroHash(token, 'zerohash');
                    if (onSuc) onSuc();
                },
                (error) => {
                    onErr?.(error);
                },
                trackingProps,
            );
        },

        createMeshTransfer: (userId, operation = 'deposit', onLaunchMesh, onSuc, onErr, trackingProps) => {
            // The only action in this store that was missing the flowGeneration guard its
            // neighbours all carry: a late response wrote the ended user's Mesh link token
            // into the store and installed an expiry interval alongside it.
            const generation = flowGeneration;
            set({ loading: true });
            getMeshLinkToken(
                userId,
                operation,
                (token, integrationTokens) => {
                    if (generation !== flowGeneration) return;
                    set({ meshLinkToken: token });
                    tokenTimer = setInterval(() => {
                        get().actions.disposeMeshLinkToken();
                    }, config.tokenExpiration);
                    onLaunchMesh(token, 'mesh', integrationTokens);
                    if (onSuc) onSuc();
                },
                (error) => {
                    if (generation !== flowGeneration) return;
                    onErr?.(error);
                    handleSnackbarError(error);
                },
                () => {
                    if (generation !== flowGeneration) return;
                    set({ loading: false });
                },
                trackingProps,
            );
        },

        getMeshWalletAddresses: (userId, authToken, type, symbol, onSuc, onErr) => {
            const generation = flowGeneration;
            getMeshWithdrawalAddresses(
                userId,
                { auth_token: authToken, institution_type: type },
                (addresses) => {
                    if (generation !== flowGeneration) return;
                    // Filter addresses by symbol to only show matching networks
                    const filteredAddresses = addresses.filter((addr) => addr.symbol === symbol);
                    set({ meshWalletAddresses: filteredAddresses });
                    onSuc(filteredAddresses);
                },
                (error) => {
                    if (generation !== flowGeneration) return;
                    onErr(error);
                    handleSnackbarError(error);
                },
            );
        },

        initiateMeshWithdrawal: async (userId, walletAddress, amountCents, network, mfaCode, onSuc, onErr, source) => {
            const generation = flowGeneration;
            const sardine = await fetchSardineSessionKey('withdrawal', userId);
            // Both clocks: !sardine.ok reports a session teardown, flowGeneration also
            // catches the unmount teardown, which leaves Sardine untouched. Issuing in
            // that window would move money with every callback suppressed as stale.
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            initiateMeshWithdrawalRequest(
                userId,
                {
                    wallet_address: walletAddress,
                    amount_cents: amountCents,
                    network,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                () => {
                    if (generation !== flowGeneration) return;
                    onSuc();
                },
                (error) => {
                    if (generation !== flowGeneration) return;
                    onErr(error);
                    handleSnackbarError(error);
                },
            );
        },

        cryptoWithdraw: async (userId, walletId, amountCents, mfaCode, onSuc, onErr, source) => {
            // ZeroHash flow for US users
            const generation = flowGeneration;
            const sardine = await fetchSardineSessionKey('withdrawal', userId);
            if (!sardine.ok || generation !== flowGeneration) return;
            const sardineSessionKey = sardine.key;
            createCryptoWithdrawal(
                userId,
                {
                    crypto_wallet_id: walletId,
                    amount_cents: amountCents,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                async (token) => {
                    if (generation !== flowGeneration) return;
                    const createZeroHashHandler = await import('utils/crypto').then((mod) => mod.createZeroHashHandler);
                    // Rechecked after the chunk load: launching the ZeroHash widget for a
                    // disposed flow would put the ended user's withdrawal in front of
                    // whoever is here now.
                    if (generation !== flowGeneration) return;
                    createZeroHashHandler(token, 'withdraw');
                    onSuc();
                },
                (error) => {
                    if (generation !== flowGeneration) return;
                    onErr(error);
                },
            );
        },

        setSelectedMeshAddress: (address) => set({ selectedMeshAddress: address }),

        setLoading: (loading) => set({ loading }),

        disposeLinkToken: () => {
            if (tokenTimer !== null) {
                clearInterval(tokenTimer);
            }
            set({ linkToken: '', linkTokenUserId: undefined, linkTokenTrackingProps: undefined });
        },

        disposeMeshLinkToken: () => {
            if (tokenTimer !== null) {
                clearInterval(tokenTimer);
            }
            set({ meshLinkToken: '' });
        },

        dispose: () => {
            // Tear the external widget down BEFORE the generation bump, and on *every* teardown
            // path -- not just a real session end.
            //
            // The bump makes each in-flight callback a no-op, so whichever teardown invalidates
            // the generation must also close the widget those callbacks belonged to. Otherwise the
            // SDK overlay outlives its own callbacks: its eventual `onExit` sees a superseded
            // token and returns early, so nothing releases the scroll lock or restores manual
            // address entry. That stranded a still-signed-in user on a `position: fixed` page
            // after navigating to a provider-less route (`stop()` -> `resetAppState(false)`).
            activeMeshLink?.closeLink();
            activeMeshLink = null;
            get().actions.releaseMeshScrollLock();
            // Bumping the generation makes every in-flight callback a no-op, including
            // the ones the citizenship dialog is waiting on, so close it here rather
            // than leaving it mounted with a Continue button that can never settle.
            flowGeneration += 1;
            useCryptoCitizenshipDialogStore.getState().actions.dispose();
            get().actions.disposeLinkToken();
            get().actions.disposeMeshLinkToken();
            if (tokenFetch !== null) {
                tokenFetch.cancel('Aborting link token create request');
                tokenFetch = null;
            }
            if (accessTokenFetch !== null) {
                accessTokenFetch.cancel('Aborting access token request');
                accessTokenFetch = null;
            }
            set({ loading: false });
        },

        currentFlowToken: () => flowGeneration,

        isFlowCurrent: (token) => token === flowGeneration,

        registerMeshLink: (handle) => {
            activeMeshLink = handle;
        },

        releaseMeshLink: (handle) => {
            if (handle !== null && activeMeshLink === handle) activeMeshLink = null;
        },

        acquireMeshScrollLock: () => {
            useScrollLockStore.getState().actions.lockScroll();
            meshHoldsScrollLock = true;
        },

        releaseMeshScrollLock: () => {
            if (!meshHoldsScrollLock) return;
            meshHoldsScrollLock = false;
            useScrollLockStore.getState().actions.unlockScroll();
        },

        endSession: () => {
            // `dispose()` closes the external widget and releases its scroll lock before bumping
            // the generation, so the widget's exit callback no-ops instead of reopening a dialog
            // for whoever signs in next. It must be teardown's job rather than the exit's: the
            // lock is a single global boolean, not ref-counted, so a late exit unlocking it would
            // free the page behind whichever Link the *next* user has open.
            get().actions.dispose();
            set({ selectedMeshAddress: null, meshWalletAddresses: [] });
        },
    },
}));
